import * as THREE from 'three'
import { gsap } from 'gsap'
import Experience from '../../../Experience'
import MeteorShower from './MeteorShower'
import galaxyVertexShader from '../Shader/galaxy/vertex.glsl'
import galaxyFragmentShader from '../Shader/galaxy/fragment.glsl'

/**
 * RingBurst — 土星がトリガーゾーンに入った時のエフェクト3層。
 *  1. 銀河スパイラル（主役）: 土星を中心に金→紫の渦巻き銀河が空一面に展開し、
 *     差動回転でゆっくり渦を巻く（Points 1ドローコール）
 *  2. リング増光: 天体本体のリングがきらめきながら光る（body.ringMaterial.uGlow）
 *  3. 流星群バースト: MeteorShower を内包して委譲（リングの氷片が流れ出すイメージ）
 *
 * エフェクト共通インターフェース: fadeIn / fadeOut / updatePosition / update / dispose
 * ※ body プロパティはマネージャが生成後にセットする（リング増光に使用）
 */
export default class RingBurst
{
  constructor(bodyConfig)
  {
    this.experience = new Experience()
    this.scene = this.experience.scene
    this.quality = this.experience.quality

    this.body = null // OrbitingCelestials が生成後にセット
    this.meteorShower = new MeteorShower(bodyConfig)

    // 銀河パーティクルの ON/OFF（celestialConfig の galaxy.enabled が初期値。
    // setGalaxyEnabled で実行中も切り替え可能）
    this.galaxyConfig = bodyConfig.galaxy ?? {}
    this.galaxyEnabled = this.galaxyConfig.enabled ?? true
    this.isActive = false // ゾーン内（fadeIn 済み）かどうか

    this.setGalaxy()
  }

  // 主役: 渦巻き銀河（three.js journey の銀河ジェネレーターパターン）
  setGalaxy()
  {
    const sizes = this.experience.sizes

    const count = this.quality.tier === 'low' ? 2500 : 6000
    const branches = 3
    const maxRadius = 26
    const randomness = 0.25

    const positions = new Float32Array(count * 3)
    const scales = new Float32Array(count)
    const offsets = new Float32Array(count)
    const radii = new Float32Array(count)

    for (let i = 0; i < count; i++) {
      // 外側ほど疎になる半径分布
      const radius = Math.pow(Math.random(), 0.6) * maxRadius
      const branchAngle = ((i % branches) / branches) * Math.PI * 2
      const spinAngle = radius * 0.28 // 外側ほど巻く

      // 腕からのばらけ（中心に寄るほど密）
      const randomX = Math.pow(Math.random(), 3) * (Math.random() < 0.5 ? 1 : -1) * randomness * radius
      const randomY = Math.pow(Math.random(), 3) * (Math.random() < 0.5 ? 1 : -1) * randomness * radius
      const randomZ = Math.pow(Math.random(), 3) * (Math.random() < 0.5 ? 1 : -1) * randomness * radius * 0.35

      positions[i * 3 + 0] = Math.cos(branchAngle + spinAngle) * radius + randomX
      positions[i * 3 + 1] = Math.sin(branchAngle + spinAngle) * radius + randomY
      positions[i * 3 + 2] = randomZ // 薄い円盤（ローカル XY 平面が銀河面）

      scales[i] = 0.4 + Math.random()
      offsets[i] = Math.random()
      radii[i] = radius
    }

    const geometry = new THREE.BufferGeometry()
    geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3))
    geometry.setAttribute('aScale', new THREE.BufferAttribute(scales, 1))
    geometry.setAttribute('aOffset', new THREE.BufferAttribute(offsets, 1))
    geometry.setAttribute('aRadius', new THREE.BufferAttribute(radii, 1))

    this.galaxyMaterial = new THREE.ShaderMaterial({
      vertexShader: galaxyVertexShader,
      fragmentShader: galaxyFragmentShader,
      uniforms: {
        uTime: new THREE.Uniform(0),
        uSize: new THREE.Uniform(270),
        uPixelRatio: new THREE.Uniform(Math.min(sizes.pixelRatio, this.quality.maxPixelRatio)),
        uSpinSpeed: new THREE.Uniform(0.5),
        uIntensity: new THREE.Uniform(0),
        uMaxRadius: new THREE.Uniform(maxRadius),
        uColorInner: new THREE.Uniform(new THREE.Color(this.galaxyConfig.colorInner ?? '#ffd27a')),
        uColorOuter: new THREE.Uniform(new THREE.Color(this.galaxyConfig.colorOuter ?? '#8a5cff')),
      },
      transparent: true,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
    })

    this.galaxyPoints = new THREE.Points(geometry, this.galaxyMaterial)
    this.galaxyGroup = new THREE.Group()
    this.galaxyGroup.add(this.galaxyPoints)
    this.galaxyGroup.renderOrder = 2
    this.galaxyGroup.visible = false
    this.scene.add(this.galaxyGroup)
  }

  // 銀河がほどけて空一面に広がる
  fadeInGalaxy()
  {
    this.galaxyGroup.visible = true
    gsap.to(this.galaxyMaterial.uniforms.uIntensity, {
      value: 1.0,
      duration: Math.max(4.0 * this.quality.motionScale, 0.01),
      ease: 'power2.inOut',
      overwrite: true,
    })
    this.galaxyGroup.scale.setScalar(0.25)
    gsap.to(this.galaxyGroup.scale, {
      x: 1, y: 1, z: 1,
      duration: Math.max(5.0 * this.quality.motionScale, 0.01),
      ease: 'power2.out',
      overwrite: true,
    })
  }

  // 銀河は縮みながら消える
  fadeOutGalaxy()
  {
    gsap.to(this.galaxyMaterial.uniforms.uIntensity, {
      value: 0,
      duration: Math.max(4.0 * this.quality.motionScale, 0.01),
      ease: 'power2.inOut',
      overwrite: true,
      onComplete: () => { this.galaxyGroup.visible = false },
    })
    gsap.to(this.galaxyGroup.scale, {
      x: 0.6, y: 0.6, z: 0.6,
      duration: Math.max(4.0 * this.quality.motionScale, 0.01),
      ease: 'power2.in',
      overwrite: true,
    })
  }

  // サブエフェクト: 銀河パーティクルの ON/OFF（発動中の切り替えにも対応）
  setGalaxyEnabled(enabled)
  {
    this.galaxyEnabled = enabled
    if (enabled && this.isActive) {
      this.fadeInGalaxy()
    } else if (!enabled) {
      this.fadeOutGalaxy()
    }
  }

  fadeIn()
  {
    this.isActive = true
    if (this.galaxyEnabled) {
      this.fadeInGalaxy()
    }

    // 本体のリングを増光
    if (this.body?.ringMaterial) {
      gsap.to(this.body.ringMaterial.uniforms.uGlow, {
        value: 1.0,
        duration: Math.max(2.5 * this.quality.motionScale, 0.01),
        ease: 'power2.inOut',
        overwrite: true,
      })
    }

    this.meteorShower.fadeIn()
  }

  fadeOut()
  {
    this.isActive = false
    this.fadeOutGalaxy()

    if (this.body?.ringMaterial) {
      gsap.to(this.body.ringMaterial.uniforms.uGlow, {
        value: 0,
        duration: Math.max(2.5 * this.quality.motionScale, 0.01),
        ease: 'power2.inOut',
        overwrite: true,
      })
    }

    this.meteorShower.fadeOut()
  }

  // 追従先（天体のワールド座標）をセット
  updatePosition(worldPosition)
  {
    // 銀河を土星に追従させ、渦の面を原点（≒カメラ）方向へ向ける
    this.galaxyGroup.position.copy(worldPosition)
    this.galaxyGroup.lookAt(0, 0, 0)
  }

  update(elapsed)
  {
    if (!this.galaxyGroup.visible) return

    this.galaxyMaterial.uniforms.uTime.value = elapsed
  }

  dispose()
  {
    this.meteorShower.dispose()
    this.scene.remove(this.galaxyGroup)
    this.galaxyPoints.geometry.dispose()
    this.galaxyMaterial.dispose()
  }
}
